import { format, isValid, parse } from 'date-fns';

import type { FilterValue, SortState, TableColumn, TableFilters } from '../types/responsiveTable';

/**
 * Pure parse/serialize of a table view (filters, sort, page, page size) to and from URL params and the saved view.
 * Everything that comes from the URL or from storage goes through `parseTableView`, which is the allow-list.
 */
export interface TableView {
  filters: TableFilters;
  sort: SortState;
  page: number;
  pageSize: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Cols = ReadonlyArray<TableColumn<any>>;
type Pair = [string, string];

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZES = [10, 25, 50];
const MAX_PAGE = 10_000;
/** The longest filter value a link or a saved view carries; the text filter input stops there too. */
export const MAX_VALUE_LENGTH = 200;
const MAX_VALUES_PER_COLUMN = 50;

const FILTER_PREFIX = 'f.';
const DAY_FORMAT = 'yyyy-MM-dd';
const NUMBER_RE = /^(equal|greater-than|less-than):(-?\d+(\.\d+)?)$/;
const PLAIN_NUMBER_RE = /^-?\d+(\.\d+)?$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}(\.\.\d{4}-\d{2}-\d{2})?$/;
const PROGRESS_RE = /^(\d{1,3})\.\.(\d{1,3})$/;
const PAGE_RE = /^[1-9]\d*$/;

const NO_SORT: SortState = { field: null, direction: 'none' };

export const isTableParam = (key: string): boolean =>
  key.startsWith(FILTER_PREFIX) || key === 'sort' || key === 'page' || key === 'size';

export const hasTableParams = (params: URLSearchParams): boolean => Array.from(params.keys()).some(isTableParam);

const isFilterable = (column: Cols[number]): boolean =>
  !column.hideColumn && column.filterable !== false && column.type !== 'avatar';

const isSortable = (column: Cols[number]): boolean => !column.hideColumn && column.type !== 'avatar' && column.sortable !== false;

const parseDay = (text: string): Date | null => {
  const date = parse(text, DAY_FORMAT, new Date());
  return isValid(date) && format(date, DAY_FORMAT) === text ? date : null;
};

const parseCalendar = (text: string): FilterValue[] | null => {
  if (!DAY_RE.test(text)) return null;
  const [startText, endText] = text.split('..');
  const start = parseDay(startText);
  if (!start) return null;
  if (endText === undefined) return [start];
  const end = parseDay(endText);
  return end && end.getTime() >= start.getTime() ? [start, end] : null;
};

const parseNumberFilter = (text: string): FilterValue | null => {
  const match = NUMBER_RE.exec(text);
  if (!match) return null;
  const value = Number(match[2]);
  // A number that would be written in exponent form (or is not finite) could not be read back.
  return PLAIN_NUMBER_RE.test(String(value)) ? { mode: match[1], value } : null;
};

const parseProgress = (text: string): FilterValue[] | null => {
  const match = PROGRESS_RE.exec(text);
  if (!match) return null;
  const min = Number(match[1]);
  const max = Number(match[2]);
  return min <= max && max <= 100 ? [min, max] : null;
};

const parseTag = (text: string): FilterValue => {
  if (text === 'true') return true;
  if (text === 'false') return false;
  return text === 'null' ? null : text;
};

const parseBoolean = (text: string, allowNull: boolean): FilterValue | undefined => {
  if (text === 'true') return true;
  if (text === 'false') return false;
  return text === 'null' && allowNull ? null : undefined;
};

const uniqueValues = (values: FilterValue[]): FilterValue[] => values.filter((value, index) => values.indexOf(value) === index);

/** Reads the raw values of one column. The values are already known to be non-empty and short. */
const parseColumnValues = (column: Cols[number], raws: string[]): FilterValue[] => {
  switch (column.type) {
    case 'string':
      return raws.slice(0, 1);
    case 'dropdown':
      return uniqueValues(raws);
    case 'tag':
      return uniqueValues(raws.map(parseTag));
    case 'boolean': {
      const allowNull = column.allowNullFilter === true;
      return uniqueValues(
        raws.map((raw) => parseBoolean(raw, allowNull)).filter((value): value is boolean | null => value !== undefined),
      );
    }
    case 'number': {
      const first = raws.map(parseNumberFilter).find((value) => value !== null);
      return first ? [first] : [];
    }
    case 'calendar':
      return raws.map(parseCalendar).find((value) => value !== null) ?? [];
    default:
      // progress (an avatar column is never filterable, so it never gets here)
      return raws.map(parseProgress).find((value) => value !== null) ?? [];
  }
};

const usableRaws = (raws: string[]): string[] =>
  raws.slice(0, MAX_VALUES_PER_COLUMN).filter((raw) => raw !== '' && raw.length <= MAX_VALUE_LENGTH);

const parseSort = (text: string | null, columns: Cols): SortState => {
  if (text === null) return NO_SORT;
  const split = text.lastIndexOf(':');
  const field = text.slice(0, split);
  const direction = text.slice(split + 1);
  const column = columns.find((candidate) => candidate.accessor === field);
  if (split < 0 || (direction !== 'asc' && direction !== 'desc') || !column || !isSortable(column)) return NO_SORT;
  return { field, direction };
};

export const parseTableView = (params: URLSearchParams, columns: Cols): TableView => {
  const filters: TableFilters = {};
  columns.filter(isFilterable).forEach((column) => {
    const values = parseColumnValues(column, usableRaws(params.getAll(`${FILTER_PREFIX}${column.accessor}`)));
    if (values.length > 0) filters[column.accessor] = values;
  });

  const pageText = params.get('page');
  const page = pageText !== null && PAGE_RE.test(pageText) && Number(pageText) <= MAX_PAGE ? Number(pageText) : 1;
  const sizeText = Number(params.get('size'));
  const pageSize = PAGE_SIZES.includes(sizeText) ? sizeText : DEFAULT_PAGE_SIZE;

  return { filters, sort: parseSort(params.get('sort'), columns), page, pageSize };
};

const formatDay = (date: Date): string => format(date, DAY_FORMAT);
const isText = (value: FilterValue): value is string => typeof value === 'string';
const isDate = (value: FilterValue): value is Date => value instanceof Date && isValid(value);
const isFlag = (value: FilterValue): value is boolean | null => typeof value === 'boolean' || value === null;

/** The raw URL text of the values of one column, before the parse rules clean it. */
const formatColumnValues = (column: Cols[number], values: FilterValue[]): string[] => {
  switch (column.type) {
    case 'string':
    case 'dropdown':
      return values.filter(isText);
    case 'tag':
      return values.filter((value) => isText(value) || isFlag(value)).map(String);
    case 'boolean':
      return values.filter(isFlag).map(String);
    case 'number': {
      const first = values.find((value) => typeof value === 'object' && value !== null && !(value instanceof Date));
      const { mode, value } = (first ?? {}) as { mode?: unknown; value?: unknown };
      return typeof mode === 'string' && typeof value === 'number' ? [`${mode}:${value}`] : [];
    }
    case 'calendar': {
      const days = values.filter(isDate).slice(0, 2).map(formatDay);
      return days.length > 0 ? [days.join('..')] : [];
    }
    default: {
      // progress (an avatar column is never filterable, so it never gets here)
      const bounds = values.filter((value) => typeof value === 'number');
      return bounds.length >= 2 ? [`${bounds[0]}..${bounds[1]}`] : [];
    }
  }
};

const formatParsedValue = (column: Cols[number], value: FilterValue): string => {
  if (column.type === 'number') {
    const { mode, value: amount } = value as { mode: string; value: number };
    return `${mode}:${amount}`;
  }
  return String(value);
};

export const serializeTableView = (view: TableView, columns: Cols): Pair[] => {
  const pairs: Pair[] = [];
  columns.filter(isFilterable).forEach((column) => {
    const values = view.filters[column.accessor];
    if (!values) return;
    // Parsing the written text again keeps only what the URL rules accept, so parse(serialize(v)) is stable.
    const kept = parseColumnValues(column, usableRaws(formatColumnValues(column, values)));
    if (column.type === 'calendar') {
      const days = kept.filter(isDate).map(formatDay);
      if (days.length > 0) pairs.push([`${FILTER_PREFIX}${column.accessor}`, days.join('..')]);
      return;
    }
    if (column.type === 'progress') {
      if (kept.length === 2) pairs.push([`${FILTER_PREFIX}${column.accessor}`, kept.join('..')]);
      return;
    }
    kept.forEach((value) => pairs.push([`${FILTER_PREFIX}${column.accessor}`, formatParsedValue(column, value)]));
  });

  if (view.sort.field !== null && view.sort.direction !== 'none') {
    pairs.push(['sort', `${view.sort.field}:${view.sort.direction}`]);
  }
  if (view.page !== 1) pairs.push(['page', String(view.page)]);
  if (view.pageSize !== DEFAULT_PAGE_SIZE) pairs.push(['size', String(view.pageSize)]);
  return pairs;
};

export const toQueryString = (pairs: Pair[]): string => new URLSearchParams(pairs).toString().replace(/%3A/gi, ':');

export const writeTableParams = (search: string, view: TableView, columns: Cols): string => {
  const others: Pair[] = Array.from(new URLSearchParams(search).entries()).filter(([key]) => !isTableParam(key));
  const query = toQueryString([...others, ...serializeTableView(view, columns)]);
  return query === '' ? '' : `?${query}`;
};

export const toSavedView = (view: TableView, columns: Cols): string => {
  const filters: TableFilters = {};
  columns.forEach((column) => {
    if (column.type !== 'string' && view.filters[column.accessor]) filters[column.accessor] = view.filters[column.accessor];
  });
  return JSON.stringify({ params: serializeTableView({ ...view, filters, page: 1 }, columns) });
};

const isPairList = (value: unknown): value is Pair[] =>
  Array.isArray(value) &&
  value.every((pair) => Array.isArray(pair) && pair.length === 2 && pair.every((part) => typeof part === 'string'));

export const parseSavedView = (json: string | null, columns: Cols): TableView | null => {
  if (json === null) return null;
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }
  const params = typeof data === 'object' && data !== null ? (data as { params?: unknown }).params : undefined;
  return isPairList(params) ? parseTableView(new URLSearchParams(params), columns) : null;
};

export const tableViewStorageKey = (userId: string, organizationId: string, tableId: string): string =>
  `table-view-${userId}-${organizationId}-${tableId}`;
